import React, { useState, useEffect, useContext } from 'react';
import { Alert, Container, CircularProgress } from '@mui/material';
import ReportsList from './ReportsList';
import KeycloakContext from '../../KeycloakProvider';
import { canAccessSidebarKey } from '../../utils/accessUtil';
import { GROUP_MATRIX } from '../../utils/accessRegistry';
import { reportError } from '../../utils/reportError';
import { baseUrl } from '../../api/baseQuery';
import { getAuthHeaders } from '../../utils/authHeaders';
import ContentUnavailable from '../../components/ContentUnavailable';

const WindsorReports = () => {
  const { keycloak } = useContext(KeycloakContext);
  const userGroups = keycloak?.tokenParsed?.realm_access?.roles || [];
  
  // Determine if user is T-Mobile, Pierson, or AT&T
  const userCustomerGroup = 
    userGroups.includes('T-Mobile') ? 'T-Mobile' :
    userGroups.includes('Pierson') ? 'Pierson' :
    userGroups.includes('AT&T') ? 'AT&T' :
    null;
  
  // Determine if user is internal (can see all reports)
  const internalGroups = ['admin', 'NOC', 'Manager', 'VP', 'Executive', 'Construction', 'Dashboard', 'C-Suite'];
  const isInternalUser = userGroups.some(role => internalGroups.includes(role));

  // Function to filter files by customer name in filename
  const filterByCustomerName = (files) => {
    // If user is internal or not from T-Mobile/Pierson/AT&T, show all files
    if (isInternalUser || !userCustomerGroup) {
      return files;
    }
    
    // Filter to only show files with customer name in filename (case-insensitive)
    return files.filter(file => {
      const filename = file.filename.toLowerCase();
      const customerName = userCustomerGroup.toLowerCase();
      return filename.includes(customerName);
    });
  };

  const hasAccess = canAccessSidebarKey(userGroups, 'kpi_windsor', GROUP_MATRIX);

  const [dailyFiles, setDailyFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }

    const fetchFiles = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders({ Accept: 'application/json' });
        const response = await fetch(`${baseUrl}/listPdfFolder/?folder=windsor`, {
          headers
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        let files = (data.files || []).filter(f => f.filename.toLowerCase().includes('daily'));
        // Apply customer name filtering
        files = filterByCustomerName(files);
        files.sort((a, b) => b.filename.localeCompare(a.filename));
        setDailyFiles(files);
        if (files.length === 0) {
          reportError({ page: 'Windsor Reports (Daily)', detail: 'listPdfFolder/?folder=windsor returned no daily files' });
        }
        setError(null);
      } catch (err) {
        setError('Failed to load Windsor reports: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterByCustomerName is recreated each render; including it would cause a refetch loop
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access Windsor Reports. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  // If customer user has no matching files after filtering, hide this page completely
  if (!loading && userCustomerGroup && (!dailyFiles || dailyFiles.length === 0)) {
    return null;
  }

  if (!loading && (!dailyFiles || dailyFiles.length === 0)) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ContentUnavailable page="Windsor Reports" />
      </Container>
    );
  }

  return (
    <ReportsList
      endpoint="getSiteReports/"
      title="Windsor Reports"
      allColumns={false}
      initialData={{ files: dailyFiles }}
      enableFilters={true}
      disableReportTypeFilter={true}
      filenamePrefix="windsor"
    />
  );
};

export default WindsorReports;
